import * as React from 'react';
import { useState, useEffect, useCallback, useMemo } from 'react';

/** Material UI Packages. */
import { Box, Button, FormControl, MenuItem, Paper, Select, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { operationRangeDaySlot } from 'shared/ui/OperationPickersDay';

/** Material UI Icons. */
import FileUploadIcon from '@mui/icons-material/FileUpload';

/** Redux Packages. */
import { useDispatch } from 'react-redux';

/** Redux Reducers. */
import { setSnackbar } from 'shared/store/slices/snackBarSlice';

/** Auth0 authenticator Packages. */
import { useAuth0 } from '@auth0/auth0-react';

/** Internal Components. */
import { dateTypeOption, operationType } from 'shared/config/constantsData';
import { customized_style } from 'shared/config/Styles';
import { callAPI } from 'shared/api/callApi';
import { CircularLoading } from 'shared/ui/CircularLoading';
import { CustomSkeleton2 } from 'shared/ui/Skeleton';
import { SelectField } from 'shared/ui/SelectForm';
import { PageHeader } from 'shared/ui/HeaderIcon';
import validateFields from 'shared/lib/validateFields';
import CustomSnackbar from 'shared/ui/CustomSnackbar';

import DataExportColumn from './components/DataExportColumn';
import DataExportTradePartnerFilters from './components/DataExportTradePartnerFilters';
import {
    EMPTY_TRADE_PARTNERS,
    optionsByRole,
    rolesForShipmentType,
    tradePartnerConflict,
    tradePartnersPayload,
} from './lib/dataExportTradePartners';

/** Initial filter setting. */
const INITIAL_FILTER = {
    shipmentType: 0,
    tradePartners: EMPTY_TRADE_PARTNERS,
    dateType: '',
    dateRange: [null, null],
    columnsList: [],
};

/** Initial validator setting. */
const INITIAL_VALIDATOR = {
    shipmentType: false,
    tradePartner: false,
    dateType: false,
    dateRange: false,
    columnsList: false,
};

/** Field styles. */
const FIELD_STYLES = {
    shipmentTypeSelect: { width: '160px', height: '32px' },
    dateTypeSelect: { width: '100px', height: '32px', fontSize: '13px', '& .MuiSelect-select': { p: 1 } },
    dateRangePicker: {
        '& .MuiInputBase-input': { px: 1, py: 0, height: '32px' },
        width: '280px',
    },
    label: {
        fontSize: '13px',
        fontWeight: 500,
        color: '#003a52',
        width: '100px',
    },
};

export default function DataExport() {
    /** Destructuring values from useAuth0 hook. */
    const { user, getAccessTokenSilently } = useAuth0();

    /** Get the dispatch function from Redux to send actions to the store. */
    const dispatch = useDispatch();

    /** State to manage the values of all input fields on the data export page. */
    const [filterResult, setFilterResult] = useState(INITIAL_FILTER);
    /** State for trade partner option. */
    const [tradePartnerList, setTradePartnerList] = useState([]);
    /** Trade partners each role can pick from. */
    const tradePartnerOptions = useMemo(() => optionsByRole(tradePartnerList), [tradePartnerList]);
    /** State for validate any inputs are empty. */
    const [validator, setValidator] = useState(INITIAL_VALIDATOR);
    /** State for columns' checkbox. */
    const [columnsCheck, setColumnsCheck] = useState(false);
    /** State for waiting API call circularLoading for downloading CSV file. */
    const [loadingCSV, setLoadingCSV] = useState(false);
    /** State for importing trade partner list loading status. */
    const [loadingTradePartner, setLoadingTradePartner] = useState(false);

    /** Named website title. */
    useEffect(() => {
        document.title = 'Data Export | Operation System';
    }, []);

    /** Call GET request when the page is first rendered. */
    useEffect(() => {
        callAPI({
            method: 'get',
            module: 'data_export',
            getAccessTokenSilently,
            email: user.email,
            onSuccess: (resp) => {
                setTradePartnerList(resp.data);
            },
            onError: () =>
                dispatch(
                    setSnackbar({
                        open: true,
                        message: 'Someting went wrong. Please try again.',
                        severity: 'error',
                    }),
                ),
            onFinal: () => setLoadingTradePartner(true),
        });
    }, [getAccessTokenSilently]);

    /** Input type's handler function. */
    const handleFilterResult = (name, newValue) => {
        setFilterResult((prev) => {
            const updates = { ...prev };

            switch (name) {
                case 'shipmentType':
                case 'dateType':
                case 'dateRange':
                    updates[name] = newValue;
                    if (name === 'shipmentType') {
                        updates.columnsList = [];
                        setColumnsCheck(false);
                        // A role the new type does not have (FTL/LTL forwarder) is dropped, not kept hidden.
                        const roleKeys = rolesForShipmentType(newValue).map((role) => role.key);
                        updates.tradePartners = Object.fromEntries(
                            Object.entries(prev.tradePartners).map(([key, list]) => [key, roleKeys.includes(key) ? list : []])
                        );
                    }
                    break;

                case 'tradePartners':
                    updates.tradePartners = newValue;
                    break;
            }
            return updates;
        });
    };

    /**
     * Handler for reset all data export states.
     * The initial dateRange value must be two null value and should not be empty because those values are used in the validate function.
     */
    const resetForm = useCallback(() => {
        setFilterResult(INITIAL_FILTER);
        setColumnsCheck(false);
        setValidator(INITIAL_VALIDATOR);
    }, []);

    /** Function for downloading CSV file. */
    const downloadCSV = useCallback((downloadUrl, fileName) => {
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }, []);

    /** Handler for creating csv file. */
    const handleCreate = () => {
        const validationRules = {
            shipmentType: () => filterResult.shipmentType !== null,
            tradePartner: () => tradePartnerConflict(filterResult.tradePartners, filterResult.shipmentType) === null,
            dateType: filterResult.dateType,
            dateRange: () => {
                if (filterResult.dateRange.includes(null)) return false;
                return filterResult.dateRange[0]?.isValid() && filterResult.dateRange[1]?.isValid();
            },
            columnsList: () => filterResult.columnsList.length > 0,
        };

        const isValid = validateFields(validationRules, setValidator);
        if (!isValid) {
            dispatch(setSnackbar({ open: true, message: 'Please complete all required fields.', severity: 'warning' }));
            return;
        }

        // Add 'Status' with any inputted columnsList.
        let columnsWithStatus = [...filterResult.columnsList];
        if (columnsWithStatus.length > 0 && !columnsWithStatus.includes('Status')) {
            columnsWithStatus.push('Status');
        }

        const postReadyFilterResult = {
            ...filterResult,
            columnsList: columnsWithStatus,
            tradePartners: tradePartnersPayload(filterResult.tradePartners, filterResult.shipmentType),
            // The exported file must cover exactly the period the user picked.
            // Leaving the dayjs range to JSON.stringify converts it to UTC, which
            // shifts the window one day for users working from Korea.
            dateRange: filterResult.dateRange.map((d) => (d ? d.format('YYYY-MM-DD') : null)),
        };

        callAPI({
            method: 'post',
            module: 'data_export',
            getAccessTokenSilently,
            email: user.email,
            obj: JSON.stringify(postReadyFilterResult),
            onInit: () => setLoadingCSV(true),
            onSuccess: (resp) => {
                const { download_url, file_name } = resp.data;
                downloadCSV(download_url, file_name);
            },
            onError: () =>
                dispatch(
                    setSnackbar({
                        open: true,
                        message: 'Someting went wrong. Please try again.',
                        severity: 'error',
                    }),
                ),
            onFinal: () => setLoadingCSV(false),
        });
    };

    /** Render shipment type field. */
    const renderShipmentTypeField = () => (
        <SelectField
            name="shipmentType"
            label="Shipment Type"
            options={operationType}
            value={filterResult.shipmentType}
            onChange={(e) => handleFilterResult('shipmentType', e.target.value)}
            sx={{ mb: 2 }}
            selectStyle={FIELD_STYLES.shipmentTypeSelect}
        />
    );

    /** Render trade partner field. */
    const renderTradePartnerField = () => (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '25px', mb: 1 }}>
            <Typography sx={{ ...FIELD_STYLES.label, lineHeight: '26px' }}>Trade Partner</Typography>
            <DataExportTradePartnerFilters
                value={filterResult.tradePartners}
                options={tradePartnerOptions}
                shipmentType={filterResult.shipmentType}
                onChange={(newValue) => handleFilterResult('tradePartners', newValue)}
                invalid={validator.tradePartner}
            />
        </Box>
    );

    /** Render the date the export goes by and its range on one line. */
    const renderDateField = () => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '25px', mt: 2 }}>
            <Typography sx={FIELD_STYLES.label}>Date</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FormControl error={validator.dateType}>
                    <Select
                        name="dateType"
                        value={filterResult.dateType}
                        displayEmpty
                        renderValue={(value) =>
                            dateTypeOption.find((o) => o.id === value)?.content ?? (
                                <Typography component="span" sx={{ fontSize: '13px', color: '#9e9e9e' }}>
                                    Date type
                                </Typography>
                            )
                        }
                        onChange={(e) => handleFilterResult('dateType', e.target.value)}
                        inputProps={{ 'aria-label': 'Date type' }}
                        sx={FIELD_STYLES.dateTypeSelect}
                    >
                        {dateTypeOption.map(({ id, content }) => (
                            <MenuItem key={id} value={id} sx={{ fontSize: '13px' }}>
                                {content}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DateRangePicker
                        value={filterResult.dateRange}
                        name="dateRange"
                        onChange={(newValue) => handleFilterResult('dateRange', newValue)}
                        slots={operationRangeDaySlot}
                        slotProps={{
                            textField: {
                                error: validator.dateRange,
                                InputLabelProps: { shrink: true },
                                name: 'dateRange',
                            },
                        }}
                        sx={FIELD_STYLES.dateRangePicker}
                    />
                </LocalizationProvider>
            </Box>
        </Box>
    );

    /** Render columns field. */
    const renderColumnsField = () => (
        <Box sx={{ display: 'flex', mt: 2, gap: '25px' }}>
            <Typography sx={FIELD_STYLES.label}>Columns</Typography>
            <DataExportColumn
                filterResult={filterResult}
                setFilterResult={setFilterResult}
                columnsCheck={columnsCheck}
                setColumnsCheck={setColumnsCheck}
                isError={validator.columnsList}
            />
        </Box>
    );

    /** Render action buttons. */
    const renderActionButtons = () => (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: '10px',
                width: '44vw',
                mt: '20px',
            }}
        >
            <Button variant="outlined" onClick={resetForm} sx={{ width: '80%' }}>
                RESET
            </Button>
            <Button variant="contained" onClick={handleCreate} sx={{ width: '80%' }}>
                CREATE
            </Button>
        </Box>
    );

    /** Render loading skeleton when the trade partner list is not loaded. */
    if (!loadingTradePartner) {
        return (
            <Paper sx={customized_style.itemPaper}>
                <CustomSkeleton2 />
            </Paper>
        );
    }

    return (
        <Paper sx={customized_style.itemPaper}>
            <CircularLoading loading={loadingCSV} />
            <PageHeader title="DATA EXPORT" icon={FileUploadIcon} />

            <Box sx={{ height: '89%', width: '100%', overflowY: 'scroll' }}>
                {renderShipmentTypeField()}
                {renderTradePartnerField()}
                {renderDateField()}
                {renderColumnsField()}
            </Box>

            {renderActionButtons()}
            <CustomSnackbar />
        </Paper>
    );
}
